<template>
  <div class="plan-card" :class="{ active: isActive, recommended: isRecommended && !isActive }">
    <div v-if="isActive" class="active-badge">
      {{ $t('plans.current') }}
    </div>
    <div v-else-if="isRecommended" class="recommended-badge">
      {{ $t('plans.popular') }}
    </div>
    <div class="plan-card__info">
      <p class="plan-card__title">{{ planTitle }}</p>

      <p class="plan-card__price header-2">{{ displayPrice }}</p>

      <p class="plan-card__description caption">{{ planDescription }}</p>
    </div>

    <primary-btn
      @click="onSelectPlan"
      :disabled="isActive || planTitle == 'Freemium'"
    >
      {{ btnLabel }}
    </primary-btn>

    <ul class="plan-card__features caption" v-if="features.length">
      <li
        class="plan-card__feature-item"
        v-for="(feature, index) in features"
        :key="index"
      >
        {{ feature }}
      </li>
    </ul>
  </div>
</template>

<script setup>
import PrimaryBtn from "@/components/BaseComponents/PrimaryBtn.vue";
import { defineProps, defineEmits, computed } from "vue";
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const props = defineProps({
  planTitle: {
    type: String,
    required: true,
  },
  planDescription: {
    type: String,
    default: "",
  },
  priceMonthly: {
    type: [Number, String],
    required: true,
  },
  priceAnnual: {
    type: [Number, String],
    required: true,
  },
  isAnnual: {
    type: Boolean,
    default: false,
  },
  features: {
    type: Array,
    default: () => [],
  },
  btnLabel: {
    type: String,
    required: true,
  },
  isActive: {
    type: Boolean,
    default: false
  },
  isRecommended: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(["selectPlan"]);

const displayPrice = computed(() => {
  const currentPrice = props.isAnnual ? props.priceAnnual : props.priceMonthly;
  if (typeof currentPrice === "number") {
    return t('plans.monthlyPrice', { price: currentPrice });
  }
  return currentPrice;
});

function onSelectPlan() {
  if (!props.isActive) {
    emit("selectPlan", {
      title: props.planTitle,
      price: props.isAnnual ? props.priceAnnual : props.priceMonthly,
      description: props.planDescription,
      features: props.features,
      paypalPlanId: props.paypalPlanId
    });
  }
}
</script>

<style scoped>
.plan-card {
  background-color: var(--color-white-10);
  border-radius: var(--border-radius-xl);
  padding: var(--spacing-lg) var(--spacing-xl);
  position: relative;
}

.plan-card__info {
  min-height: 180px;
  margin-bottom: var(--spacing-lg);
}

.plan-card__title {
  color: var(--color-white-70);
}

.plan-card__price {
  margin-bottom: var(--spacing-sm);
}

.plan-card__description {
  color: var(--color-white-70);
  margin-bottom: var(--spacing-xl);
  font-size: var(--font-size-sm);
  min-height: 3em;
}

.plan-card primary-btn {
  margin-bottom: var(--spacing-lg);
}

.plan-card__features {
  list-style: disc;
  padding: 0;
  padding-left: var(--spacing-xl);
  margin-top: var(--spacing-lg);
}

.plan-card__feature-item {
  text-align: left;
  margin: var(--spacing-xs) 0;
  color: var(--color-white-70);
}

.plan-card__button {
  width: 100%;
  padding: var(--spacing-md);
  background-color: var(--color-primary);
  color: var(--color-white);
  border: none;
  border-radius: var(--border-radius-xs);
  cursor: pointer;
}

.plan-card__button:hover {
  background-color: var(--color-primary-dark);
}

.active-badge {
  position: absolute;
  top: -12px;
  right: -12px;
  background: var(--gradient-primary);
  color: var(--color-white);
  padding: var(--spacing-xs) var(--spacing-sm);
  border-radius: var(--border-radius-md);
  font-size: var(--font-size-sm);
  font-weight: 500;
}

.recommended-badge {
  position: absolute;
  top: -12px;
  right: -12px;
  background: var(--gradient-success);
  color: var(--color-white);
  padding: var(--spacing-xs) var(--spacing-sm);
  border-radius: var(--border-radius-md);
  font-size: var(--font-size-sm);
  font-weight: 500;
}

.plan-card.recommended {
  position: relative;
  border: 1px solid var(--color-accent-primary);
}

.plan-card.active {
  position: relative;
  border: none;
}

.plan-card.active::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: var(--border-radius-xl);
  padding: 2px;
  background: var(--gradient-primary);
  -webkit-mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  -webkit-mask-composite: xor;
  mask-composite: exclude;
  pointer-events: none;
}
</style>
